import React, { useState } from 'react';
import { clients } from '../../data/clients';
import { forecasts } from '../../data/forecasts';
import { MapPin, CloudSnow, X, Phone, Mail, AlertCircle } from 'lucide-react';
export function PlanningMap() {
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const getClientForecasts = (clientId: string) => {
    return forecasts.filter(f => f.clientId === clientId);
  };
  const selectedClientData = selectedClient ? clients.find(c => c.id === selectedClient) : null;
  const selectedClientForecasts = selectedClient ? getClientForecasts(selectedClient) : [];
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-386">
      <div className="flex items-center justify-between mb-4" data-id="element-387">
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-388">
          Carte des clients et prévisions
        </h2>
        <div className="flex items-center space-x-2" data-id="element-389">
          <button className="px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-700 rounded-lg" data-id="element-390">
            Vue régionale
          </button>
          <button className="px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200" data-id="element-391">
            Vue satellite
          </button>
        </div>
      </div>
      <div className="relative w-full h-[600px] bg-gradient-to-br from-blue-50 to-gray-100 rounded-lg overflow-hidden" data-id="element-392">
        {/* Simulated Weather Overlay */}
        <div className="absolute inset-0 pointer-events-none" data-id="element-393">
          <div className="absolute top-1/4 left-1/4 w-48 h-48 bg-blue-200 rounded-full opacity-40 blur-3xl" data-id="element-394"></div>
          <div className="absolute top-1/2 right-1/3 w-56 h-56 bg-indigo-200 rounded-full opacity-40 blur-3xl" data-id="element-395"></div>
          <div className="absolute bottom-1/4 left-1/2 w-52 h-52 bg-purple-200 rounded-full opacity-40 blur-3xl" data-id="element-396"></div>
        </div>
        {/* Client Markers */}
        {clients.map(client => {
        const clientForecasts = getClientForecasts(client.id);
        const hasForecasts = clientForecasts.length > 0;
        const highestIntensity = clientForecasts.reduce((max, f) => {
          if (f.intensity === 'forte') return 'forte';
          if (f.intensity === 'modérée' && max !== 'forte') return 'modérée';
          return max;
        }, 'légère' as string);
        // Convert coordinates to percentage positions (simplified projection)
        const latRange = {
          min: 46.7,
          max: 46.9
        };
        const lngRange = {
          min: -71.4,
          max: -71.15
        };
        const x = (client.address.coordinates.lng - lngRange.min) / (lngRange.max - lngRange.min) * 100;
        const y = (latRange.max - client.address.coordinates.lat) / (latRange.max - latRange.min) * 100;
        return <button key={client.id} onClick={() => setSelectedClient(client.id)} className="absolute transform -translate-x-1/2 -translate-y-1/2 group" style={{
          left: `${x}%`,
          top: `${y}%`
        }} data-id="element-397">
              <div className="relative" data-id="element-398">
                {/* Marker */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-transform group-hover:scale-110 ${client.priority === 'high' ? 'bg-red-500' : client.priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'}`} data-id="element-399">
                  <MapPin className="w-5 h-5 text-white" data-id="element-400" />
                </div>
                {/* Forecast Indicator */}
                {hasForecasts && <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center shadow-md ${highestIntensity === 'forte' ? 'bg-red-600' : highestIntensity === 'modérée' ? 'bg-orange-500' : 'bg-blue-500'}`} data-id="element-401">
                    <CloudSnow className="w-3 h-3 text-white" data-id="element-402" />
                  </div>}
                {/* Pulse Effect for Active Forecasts */}
                {hasForecasts && <div className={`absolute inset-0 rounded-full animate-ping opacity-75 ${highestIntensity === 'forte' ? 'bg-red-500' : highestIntensity === 'modérée' ? 'bg-orange-500' : 'bg-blue-500'}`} data-id="element-403"></div>}
                {/* Label on Hover */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-404">
                  <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap" data-id="element-405">
                    {client.name}
                  </div>
                </div>
              </div>
            </button>;
      })}
        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-4 space-y-3" data-id="element-406">
          <div data-id="element-407">
            <p className="text-xs font-semibold text-gray-700 mb-2" data-id="element-408">
              Priorité client
            </p>
            <div className="space-y-1.5" data-id="element-409">
              <div className="flex items-center space-x-2" data-id="element-410">
                <div className="w-4 h-4 bg-red-500 rounded-full" data-id="element-411"></div>
                <span className="text-xs text-gray-700" data-id="element-412">Haute</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-413">
                <div className="w-4 h-4 bg-yellow-500 rounded-full" data-id="element-414"></div>
                <span className="text-xs text-gray-700" data-id="element-415">Moyenne</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-416">
                <div className="w-4 h-4 bg-blue-500 rounded-full" data-id="element-417"></div>
                <span className="text-xs text-gray-700" data-id="element-418">Basse</span>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-3" data-id="element-419">
            <p className="text-xs font-semibold text-gray-700 mb-2" data-id="element-420">
              Intensité neige
            </p>
            <div className="space-y-1.5" data-id="element-421">
              <div className="flex items-center space-x-2" data-id="element-422">
                <CloudSnow className="w-4 h-4 text-red-600" data-id="element-423" />
                <span className="text-xs text-gray-700" data-id="element-424">Forte (10+ cm)</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-425">
                <CloudSnow className="w-4 h-4 text-orange-500" data-id="element-426" />
                <span className="text-xs text-gray-700" data-id="element-427">Modérée (5-10 cm)</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-428">
                <CloudSnow className="w-4 h-4 text-blue-500" data-id="element-429" />
                <span className="text-xs text-gray-700" data-id="element-430">Légère (2-5 cm)</span>
              </div>
            </div>
          </div>
        </div>
        {/* Map Controls */}
        <div className="absolute bottom-4 right-4 flex flex-col space-y-2" data-id="element-431">
          <button className="w-10 h-10 bg-white rounded-lg shadow-lg flex items-center justify-center text-gray-600 hover:bg-gray-50 font-semibold" data-id="element-432">
            +
          </button>
          <button className="w-10 h-10 bg-white rounded-lg shadow-lg flex items-center justify-center text-gray-600 hover:bg-gray-50 font-semibold" data-id="element-433">
            −
          </button>
        </div>
      </div>
      {/* Client Detail Panel */}
      {selectedClientData && <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl border-l border-gray-200 z-50 overflow-y-auto" data-id="element-434">
          <div className="p-6" data-id="element-435">
            <div className="flex items-start justify-between mb-6" data-id="element-436">
              <div data-id="element-437">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-438">
                  {selectedClientData.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-439">
                  {selectedClientData.address.street}
                  <br data-id="element-440" />
                  {selectedClientData.address.city},{' '}
                  {selectedClientData.address.postalCode}
                </p>
              </div>
              <button onClick={() => setSelectedClient(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-441">
                <X className="w-5 h-5 text-gray-500" data-id="element-442" />
              </button>
            </div>
            {/* Client Info */}
            <div className="space-y-4 pb-6 border-b border-gray-200" data-id="element-443">
              <div className="flex items-center justify-between" data-id="element-444">
                <span className="text-sm text-gray-600" data-id="element-445">Priorité</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${selectedClientData.priority === 'high' ? 'bg-red-100 text-red-700' : selectedClientData.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-446">
                  {selectedClientData.priority === 'high' ? 'Haute' : selectedClientData.priority === 'medium' ? 'Moyenne' : 'Basse'}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-447">
                <span className="text-sm text-gray-600" data-id="element-448">SLA</span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-449">
                  {selectedClientData.slaHours} heures
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-450">
                <span className="text-sm text-gray-600" data-id="element-451">Type de contrat</span>
                <span className="text-sm font-medium text-gray-900 capitalize" data-id="element-452">
                  {selectedClientData.contractType}
                </span>
              </div>
            </div>
            {/* Contact Info */}
            <div className="py-6 border-b border-gray-200" data-id="element-453">
              <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-454">
                Contact
              </h4>
              <div className="space-y-2" data-id="element-455">
                <p className="text-sm text-gray-700" data-id="element-456">
                  {selectedClientData.contact.name}
                </p>
                <div className="flex items-center space-x-2 text-sm text-gray-600" data-id="element-457">
                  <Phone className="w-4 h-4" data-id="element-458" />
                  <span data-id="element-459">{selectedClientData.contact.phone}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600" data-id="element-460">
                  <Mail className="w-4 h-4" data-id="element-461" />
                  <span data-id="element-462">{selectedClientData.contact.email}</span>
                </div>
              </div>
            </div>
            {/* Forecasts */}
            <div className="py-6" data-id="element-463">
              <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-464">
                Prévisions ({selectedClientForecasts.length})
              </h4>
              {selectedClientForecasts.length > 0 ? <div className="space-y-3" data-id="element-465">
                  {selectedClientForecasts.map(forecast => <div key={forecast.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-466">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-467">
                        <CloudSnow className={`w-4 h-4 ${forecast.intensity === 'forte' ? 'text-red-600' : forecast.intensity === 'modérée' ? 'text-orange-500' : 'text-blue-500'}`} data-id="element-468" />
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${forecast.intensity === 'forte' ? 'bg-red-100 text-red-700' : forecast.intensity === 'modérée' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-469">
                          {forecast.intensity}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1" data-id="element-470">
                        <p data-id="element-471">
                          Accumulation: {forecast.accumulation.min}-
                          {forecast.accumulation.max} cm
                        </p>
                        <p data-id="element-472">Probabilité: {forecast.probability}%</p>
                        <p data-id="element-473">Température: {forecast.temperature}°C</p>
                      </div>
                      {forecast.triggeredIntervention && <div className="mt-2 flex items-center space-x-1 text-xs text-green-700" data-id="element-474">
                          <AlertCircle className="w-3 h-3" data-id="element-475" />
                          <span data-id="element-476">Intervention déclenchée</span>
                        </div>}
                    </div>)}
                </div> : <p className="text-sm text-gray-500" data-id="element-477">Aucune prévision active</p>}
            </div>
          </div>
        </div>}
    </div>;
}